import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, IconButton, Chip } from '@mui/material';
import {
    ArrowForward as ArrowForwardIcon, Search as SearchIcon
} from '@mui/icons-material';
import ContactAuditDialog from '@components/contact/ContactAuditDialog';
import SendoutIsTestChip from '@components/sendout/SendoutIsTestChip';
import SendoutContactStatusChip from '@components/sendout/SendoutContactStatusChip';
import MailContent from '@components/MailContent';
import SimulateSmsResponse from '@components/contact/SimulateSmsResponse';
import { nl2br, extractBestTranslation } from '@utils/helpers';
const ContactAuditAction = ({ audit, showDetails, reloadContact, autoOpenDetails = false, ...params }) => {
    const { t, i18n } = useTranslation();
    const [modalId, setModalId] = useState(autoOpenDetails ? audit.id : null);

    const getRecipientType = () => {
        switch (audit.details.recipientType ?? 'contact') {
            case 'contact':
            default:
                return t('Email sent to contact');
            case 'user':
                return t('Email sent to user who created the sendout');
            case 'other':
                return t('Email sent to a third party');
        }
    };

    switch (audit.action) {
        case 'start':
            return (
                <div>
                    <Typography>{t('Start processing')}</Typography>
                    {showDetails &&
                        <Typography variant="body2" color="textSecondary">
                            {audit.details.pointers.join(', ')}
                        </Typography>
                    }
                </div>
            );

        case 'start_new_process':
            return t('Starting a new process');

        case 'release_lock':
            return t('Processing did not succeed: unlocking');

        case 'finished':
            return t('Processing finished');

        case 'change_pointer':
            return (
                <div>
                    <Typography>{t('Next step')}</Typography>
                    <Typography variant="body2" color="textSecondary">
                        {audit.details.old} <ArrowForwardIcon sx={{ fontSize: 'medium', verticalAlign: 'top' }} /> {audit.details.new}
                    </Typography>
                </div>
            );

        case 'change_data':
            return (
                <div>
                    <Typography>{t('Data received')}</Typography>
                    <Typography variant="body2" color="textSecondary">
                        {typeof audit.details.new === 'object' ?
                            JSON.stringify(audit.details.new, null, 2)
                            : audit.details.new}
                    </Typography>
                </div>
            );

        case 'change_status':
            return (
                <div>
                    <Typography>{t('Status changed')}</Typography>
                    <Typography variant="body2" color="textSecondary">
                        <SendoutContactStatusChip status={audit.details.old} />
                        <ArrowForwardIcon sx={{ fontSize: 'medium', verticalAlign: 'middle' }} />
                        <SendoutContactStatusChip status={audit.details.new} />
                    </Typography>
                </div>
            );


        case 'change_extra':
            return (
                <div>
                    <Typography>{t('Contact data changed')}</Typography>
                    <Typography variant="body2" color="textSecondary">
                        {JSON.stringify(audit.details, null, 2)}
                    </Typography>
                </div>
            );

        case 'send_mail':
            return (
                <div>
                    <Box display="flex" alignItems="start">
                        <Box>
                            <Typography>{getRecipientType()}</Typography>
                            <Typography variant="body2" color="textSecondary">
                                {(() => {
                                    switch (audit.details.recipientType ?? 'contact') {
                                        case 'contact':
                                        default:
                                            return audit.details.subject;
                                        case 'user':
                                        case 'other':
                                            return audit.details.recipients ? audit.details.recipients.join(', ') : null;
                                    }
                                })()}
                            </Typography>
                        </Box>
                        <IconButton size="small" onClick={() => setModalId(audit.id)} sx={{ ml: 1, position: 'relative', top: -5 }}>
                            <SearchIcon />
                        </IconButton>
                    </Box>
                    <ContactAuditDialog audit={audit} modalId={modalId} setModalId={setModalId} width="md">
                        <Typography variant="h2">
                            {getRecipientType()}
                            <SendoutIsTestChip is_test={audit.details.is_test} sx={{ ml: 2 }} />
                            {!audit.details.is_test && audit.details.credits && 
                                <Chip label={t('{{nb}} {{type}} credit used', {nb: audit.details.credits, type: t('credit.email')})}
                                    size="small"
                                    sx={{ ml: 1 }}
                                />
                            }
                        </Typography>
                        <Typography variant="h4" sx={{ mt: 2 }}>
                            {t('Recipient')} : {audit.details.recipients ? audit.details.recipients.join(', ') : null}
                        </Typography>
                        <Typography variant="h4" sx={{ mt: 1 }}>
                            {t('Subject')} : {audit.details.subject}
                        </Typography>
                        <Box sx={{ mt: 2 }}>
                            <MailContent htmlContent={audit.details.content} />
                        </Box>
                    </ContactAuditDialog>
                </div>
            );

        case 'send_sms':
            return (
                <div>
                    <Box display="flex" alignItems="start">
                        <Box>
                            <Typography>{t('SMS sent')}</Typography>
                        </Box>
                        <IconButton size="small" onClick={() => setModalId(audit.id)} sx={{ ml: 1, position: 'relative', top: -5 }}>
                            <SearchIcon />
                        </IconButton>
                    </Box>
                    <ContactAuditDialog audit={audit} modalId={modalId} setModalId={setModalId} width="xs">
                        <Typography variant="h2">
                            {t('SMS sent')}
                            <SendoutIsTestChip is_test={audit.details.is_test} sx={{ ml: 2 }} />
                            {!audit.details.is_test && audit.details.credits && 
                                <Chip
                                    label={audit.details.credits > 1 
                                        ? t('{{nb}} {{type}} credits used', {nb: audit.details.credits, type: t('credit.sms')}) 
                                        : t('{{nb}} {{type}} credit used', {nb: audit.details.credits, type: t('credit.sms')})}
                                    size="small"
                                    sx={{ ml: 1 }}
                                />
                            }
                        </Typography>
                        <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                            {nl2br(audit.details.content)}
                        </Typography>
                        {audit.details.simulateSmsUrl &&
                            <SimulateSmsResponse url={audit.details.simulateSmsUrl} sx={{ mt: 2 }} onSuccess={() => {
                                reloadContact();
                                setModalId(null);
                            }} />
                        }
                    </ContactAuditDialog>
                </div>
            );

        case 'waiting_for_action':
            return (
                <div>
                    <Typography>

                        {(() => {
                            switch (audit.details.actionType) {
                                case 'user-button':
                                    return t('Waiting for user action');
                                case 'contact-button':
                                default:
                                    return t('Waiting for contact action');
                            }
                        })()}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                        {audit.details.form && (
                            t('Fill in the form') + (audit.details.actions.length ? ' - ' : '')
                        )}
                        {audit.details.actions.join(' - ')}
                    </Typography>
                </div>
            );

        case 'waiting_for_retry':
            return (
                <div>
                    <Typography>{t('Retry in {{minutes}} minutes', {minutes: audit.details.minutes})}</Typography>
                </div>
            );

        case 'action_received':
            return (
                <div>
                    <Typography>
                        {audit.details.user ?
                            t('User action') + ` (${audit.details.user.first_name} ${audit.details.user.last_name})`
                            : t('Contact action')
                        }
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                        {typeof audit.details.data === 'object' ? t('Form completed') : audit.details.data}
                    </Typography>
                </div>
            );

        case 'default_flow':
            return (
                <div>
                    <Typography>{t('Action timeout exceeded')}</Typography>
                    <Typography variant="body2" color="textSecondary">
                        {t('Executing default flow')}
                    </Typography>
                </div>
            );

        case 'external':
            return (
                <div>
                    <Typography>{extractBestTranslation(audit.details.action, i18n.language)}</Typography>
                    {showDetails && audit.details.url &&
                        <Typography variant="body2" color="textSecondary">
                            {t('URL')} : {audit.details.url}
                        </Typography>
                    }
                    <Typography variant="body2" color="textSecondary">
                        {audit.details.error && t('Error') + ' : '}
                        {nl2br(t(audit.details.message, audit.details.messageParams ?? {}))}
                    </Typography>
                </div>
            );

        case 'error':
            return (
                <div>
                    <Typography>{t('Error')}</Typography>
                    {audit.details.message &&
                        <Typography variant="body2" color="textSecondary">
                            {t(audit.details.message)}
                        </Typography>
                    }
                </div>
            );

        default:
            return t('Unknown action');
    }
};

export default ContactAuditAction;